import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react'
import { DocumentCommentThread, DocumentChangeSet, DocumentComment } from '../types'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { reviewAPI } from '@api/review'
import { useDocumentStore } from './documentStore'
import { logError } from '@utils/logger'
import toast from 'react-hot-toast'

interface ReviewContextType {
    // State
    comments: DocumentCommentThread[]
    history: DocumentChangeSet[]
    trackChanges: DocumentChangeSet[]
    isLoadingComments: boolean
    isLoadingHistory: boolean
    activeTab: 'comments' | 'history' | 'track-changes' | 'ai'
    showSidebar: boolean
    activeThreadId: string | null

    // Actions
    createThread: (anchor: any, body: string) => Promise<void>
    replyToThread: (threadId: string, body: string) => Promise<void>
    resolveThread: (threadId: string) => Promise<void>
    deleteComment: (commentId: string) => Promise<void>
    setActiveTab: (tab: 'comments' | 'history' | 'track-changes' | 'ai') => void
    setActiveThreadId: (threadId: string | null) => void
    setShowSidebar: (show: boolean) => void
    refreshComments: () => void
    refreshHistory: () => void
}

const ReviewContext = createContext<ReviewContextType | undefined>(undefined)

type ReviewAction =
    | { type: 'SET_ACTIVE_TAB'; payload: 'comments' | 'history' | 'track-changes' | 'ai' }
    | { type: 'SET_SHOW_SIDEBAR'; payload: boolean }
    | { type: 'SET_ACTIVE_THREAD_ID'; payload: string | null }

interface ReviewStateType {
    activeTab: 'comments' | 'history' | 'track-changes' | 'ai'
    showSidebar: boolean
    activeThreadId: string | null
}

const initialState: ReviewStateType = {
    activeTab: 'comments',
    showSidebar: false,
    activeThreadId: null
}

function reviewReducer(state: ReviewStateType, action: ReviewAction): ReviewStateType {
    switch (action.type) {
        case 'SET_ACTIVE_TAB':
            return { ...state, activeTab: action.payload }
        case 'SET_SHOW_SIDEBAR':
            return { ...state, showSidebar: action.payload }
        case 'SET_ACTIVE_THREAD_ID':
            return { ...state, activeThreadId: action.payload }
        default:
            return state
    }
}

export const ReviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [state, dispatch] = useReducer(reviewReducer, initialState)
    const queryClient = useQueryClient()
    const { currentDocument } = useDocumentStore()
    const documentId = currentDocument?.id

    // Fetch Comments
    const { data: comments = [], isLoading: isLoadingComments } = useQuery({
        queryKey: ['comments', documentId],
        queryFn: () => documentId ? reviewAPI.getComments(documentId) : Promise.resolve([]),
        enabled: !!documentId,
        staleTime: 1000 * 30 // 30 seconds
    })

    // Fetch History/Track Changes
    // Fetching history/track changes when tab is active or just eagerly
    const { data: history = [], isLoading: isLoadingHistory } = useQuery({
        queryKey: ['history', documentId],
        queryFn: () => documentId ? reviewAPI.getHistory(documentId) : Promise.resolve([]),
        enabled: !!documentId && (state.activeTab === 'history' || state.activeTab === 'track-changes'),
    })

    const { data: trackChanges = [] } = useQuery({
        queryKey: ['trackChanges', documentId],
        queryFn: () => documentId ? reviewAPI.getTrackChanges(documentId) : Promise.resolve([]),
        enabled: !!documentId && state.activeTab === 'track-changes',
    })

    // Mutations
    const createThreadMutation = useMutation({
        mutationFn: (data: { anchor: any, body: string }) =>
            reviewAPI.createThread(documentId!, data.anchor, data.body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comments', documentId] })
            toast.success('Comment added')
        },
        onError: (error) => {
            logError('Failed to create thread:', error)
            toast.error('Failed to add comment')
        }
    })

    const replyMutation = useMutation({
        mutationFn: (data: { threadId: string, body: string }) =>
            reviewAPI.replyToThread(documentId!, data.threadId, data.body),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['comments', documentId] })
    })

    const resolveMutation = useMutation({
        mutationFn: (threadId: string) => reviewAPI.resolveThread(documentId!, threadId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['comments', documentId] })
            toast.success('Thread resolved')
        }
    })

    const deleteMutation = useMutation({
        mutationFn: (commentId: string) => reviewAPI.deleteComment(documentId!, commentId),
        onSuccess: () => queryClient.invalidateQueries({ queryKey: ['comments', documentId] })
    })

    // Actions
    const createThread = useCallback(async (anchor: any, body: string) => {
        if (!documentId) return
        await createThreadMutation.mutateAsync({ anchor, body })
    }, [documentId, createThreadMutation])

    const replyToThread = useCallback(async (threadId: string, body: string) => {
        if (!documentId) return
        await replyMutation.mutateAsync({ threadId, body })
    }, [documentId, replyMutation])

    const resolveThread = useCallback(async (threadId: string) => {
        if (!documentId) return
        await resolveMutation.mutateAsync(threadId)
    }, [documentId, resolveMutation])

    const deleteComment = useCallback(async (commentId: string) => {
        if (!documentId) return
        await deleteMutation.mutateAsync(commentId)
    }, [documentId, deleteMutation])

    const setActiveTab = useCallback((tab: 'comments' | 'history' | 'track-changes' | 'ai') => {
        dispatch({ type: 'SET_ACTIVE_TAB', payload: tab })
    }, [])

    const setShowSidebar = useCallback((show: boolean) => {
        dispatch({ type: 'SET_SHOW_SIDEBAR', payload: show })
    }, [])

    const setActiveThreadId = useCallback((threadId: string | null) => {
        dispatch({ type: 'SET_ACTIVE_THREAD_ID', payload: threadId })
    }, [])

    const refreshComments = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['comments', documentId] })
    }, [queryClient, documentId])

    const refreshHistory = useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['history', documentId] })
    }, [queryClient, documentId])

    const value: ReviewContextType = {
        comments,
        history,
        trackChanges,
        isLoadingComments,
        isLoadingHistory,
        activeTab: state.activeTab,
        showSidebar: state.showSidebar,
        activeThreadId: state.activeThreadId,
        createThread,
        replyToThread,
        resolveThread,
        deleteComment,
        setActiveTab,
        setShowSidebar,
        setActiveThreadId,
        refreshComments,
        refreshHistory
    }

    return (
        <ReviewContext.Provider value={value}>
            {children}
        </ReviewContext.Provider>
    )
}

export const useReviewStore = () => {
    const context = useContext(ReviewContext)
    if (context === undefined) {
        throw new Error('useReviewStore must be used within a ReviewProvider')
    }
    return context
}
